import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetClientsQuery, useCreateClientMutation, useDeleteClientMutation } from '../services/clientsApi';
import { useGetUserInfoQuery } from '../services/authApi';
import { useDemoData } from '../context/DemoDataContext';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Skeleton } from './components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Eye, Trash2, Loader2, Users, Building2, Activity, Database } from 'lucide-react';

const getRiskBadgeVariant = (category) => {
  switch (category) {
    case 'Low': return 'default';
    case 'Medium': return 'secondary';
    case 'High': return 'destructive';
    default: return 'outline';
  }
};

const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'active': return 'default';
    case 'defaulted': return 'destructive';
    case 'closed': return 'secondary';
    default: return 'outline';
  }
};

export function Clients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { demoClients, isDemoMode, loadDemoData, clearDemoData } = useDemoData();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [clientType, setClientType] = useState('Persona Moral');

  const { data: userInfo, isLoading: isUserInfoLoading } = useGetUserInfoQuery();

  const handleAddClientClick = () => {
    if (isUserInfoLoading) {
      toast.info('Verifying profile details...');
      return;
    }

    if (userInfo && userInfo.hasInfo) {
      setDialogOpen(true);
    } else {
      setShowWarningModal(true);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    middle_name: '',
    last_name: '',
    fiscal_id: '',
    fiscal_regime: '601',
    fiscal_address: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    zip_code: '',
    exterior_number: '',
    interior_number: '',
    neighborhood: '',
    municipality: '',
    nacionality: 'Mexicana',
    country: 'México',
    loan_amount: '',
    loan_type: 'Working Capital',
    risk_score: '',
    risk_category: 'Medium',
    status: 'active',
    industry: '',
    annual_revenue: '',
    employees: '',
    established: ''
  });

  // RTK Query hooks
  const { data: clients = [], isLoading } = useGetClientsQuery();
  const [createClient, { isLoading: isSubmitting }] = useCreateClientMutation();
  const [deleteClient] = useDeleteClientMutation();
  const clientsToRender = (clients && clients.length > 0) ? clients : (isDemoMode ? demoClients : []);

  const handleOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setCurrentStep(1);
      setClientType('Persona Moral');
      setFormData({
        name: '',
        middle_name: '',
        last_name: '',
        fiscal_id: '',
        fiscal_regime: '601',
        fiscal_address: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        zip_code: '',
        exterior_number: '',
        interior_number: '',
        neighborhood: '',
        municipality: '',
        nacionality: 'Mexicana',
        country: 'México',
        loan_amount: '',
        loan_type: 'Working Capital',
        risk_score: '',
        risk_category: 'Medium',
        status: 'active',
        industry: '',
        annual_revenue: '',
        employees: '',
        established: ''
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let mappedFiscalRegime = 'PM';
    if (clientType === 'Persona Física con Actividad Empresarial') {
      mappedFiscalRegime = 'PFAE';
    } else if (clientType === 'Persona Física') {
      mappedFiscalRegime = 'PF';
    }

    try {
      const payload = {
        ...formData,
        fiscal_regime: mappedFiscalRegime,
        loan_amount: parseFloat(formData.loan_amount) || 0,
        risk_score: parseInt(formData.risk_score) || 50,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        employees: formData.employees ? parseInt(formData.employees) : null,
        established: formData.established || null
      };

      await createClient(payload).unwrap();
      toast.success('Client created successfully');
      handleOpenChange(false);
    } catch (error) {
      toast.error(error?.data?.detail || error?.message || 'Failed to create client');
    }
  };

  const handleDelete = async (clientId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      await deleteClient(clientId).unwrap();
      toast.success('Client deleted');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const getClientDisplayName = (c) => {
    const hasPersonDetails = c.middle_name || c.last_name;
    if (hasPersonDetails && (!c.fiscal_id || c.fiscal_id.length === 13)) {
      return `${c.name || ''} ${c.middle_name || ''} ${c.last_name || ''}`.replace(/\s+/g, ' ').trim();
    }
    return c.name || 'N/A';
  };

  const getClientSubName = (c) => {
    const hasPersonDetails = c.middle_name || c.last_name;
    if (hasPersonDetails && (!c.fiscal_id || c.fiscal_id.length === 13)) {
      return t('clients.personaFisica');
    }
    if (c.middle_name || c.last_name) {
      return `Rep: ${c.middle_name || ''} ${c.last_name || ''}`.replace(/\s+/g, ' ').trim();
    }
    return t('clients.personaMoral');
  };

  const getTranslatedStatus = (status) => {
    switch (status) {
      case 'active': return t('clients.active');
      case 'pending': return t('clients.pending');
      case 'defaulted': return t('clients.defaulted');
      case 'closed': return t('clients.closed');
      case 'review': return t('clients.review');
      default: return status;
    }
  };

  const getTranslatedRisk = (category) => {
    switch (category) {
      case 'Low': return t('clients.lowRisk');
      case 'Medium': return t('clients.mediumRisk');
      case 'High': return t('clients.highRisk');
      default: return category;
    }
  };

  const getTranslatedLoanType = (loanType) => {
    switch (loanType) {
      case 'Working Capital': return t('clients.workingCapital');
      case 'Business Expansion': return t('clients.businessExpansion');
      case 'Equipment Financing': return t('clients.equipmentFinancing');
      case 'Fleet Financing': return t('clients.fleetFinancing');
      case 'Project Financing': return t('clients.projectFinancing');
      case 'Renovation Loan': return t('clients.renovationLoan');
      default: return loanType;
    }
  };

  const filteredClients = clientsToRender.filter(client => {
    const displayName = getClientDisplayName(client).toLowerCase();
    const subName = getClientSubName(client).toLowerCase();
    const email = (client.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return displayName.includes(search) || subName.includes(search) || email.includes(search);
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="clients-loading">
        <div className="flex justify-between items-center mb-8">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('clients.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('clients.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('clients.addClient')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  Add New Client {currentStep === 2 && `(${clientType})`}
                </DialogTitle>
                <DialogDescription>
                  {currentStep === 1
                    ? 'Step 1: Select the category of client/taxpayer'
                    : 'Step 2: Enter the client information below'}
                </DialogDescription>
              </DialogHeader>

              {currentStep === 1 ? (
                <div className="space-y-6 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientType">Client Category / Taxpayer Type</Label>
                    <Select value={clientType} onValueChange={setClientType}>
                      <SelectTrigger data-testid="client-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Persona Moral">Persona Moral (Corporation / Company)</SelectItem>
                        <SelectItem value="Persona Física con Actividad Empresarial">
                          Persona Física con Actividad Empresarial (Sole Proprietorship)
                        </SelectItem>
                        <SelectItem value="Persona Física">Persona Física (Individual / Employee)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-border/50">
                    <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="button" className="bg-primary hover:bg-primary/90" onClick={() => setCurrentStep(2)}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Render different fields depending on clientType */}
                    {clientType === 'Persona Moral' ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="name">Company Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="e.g. TechFlow Solutions"
                            data-testid="client-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="established">Date of Establishment</Label>
                          <Input
                            id="established"
                            name="established"
                            type="text"
                            value={formData.established}
                            onChange={handleInputChange}
                            placeholder="DD/MM/YYYY"
                            data-testid="client-established-input"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="name">First Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="First Name"
                            data-testid="client-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="middle_name">Middle Name</Label>
                          <Input
                            id="middle_name"
                            name="middle_name"
                            value={formData.middle_name}
                            onChange={handleInputChange}
                            placeholder="Middle Name"
                            data-testid="client-middle-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last_name">Last Name *</Label>
                          <Input
                            id="last_name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            required
                            placeholder="Last Name"
                            data-testid="client-last-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="established">Birthdate</Label>
                          <Input
                            id="established"
                            name="established"
                            type="text"
                            value={formData.established}
                            onChange={handleInputChange}
                            placeholder="DD/MM/YYYY"
                            data-testid="client-established-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nacionality">Nationality</Label>
                          <Input
                            id="nacionality"
                            name="nacionality"
                            value={formData.nacionality}
                            onChange={handleInputChange}
                            placeholder="e.g. Mexicana"
                            data-testid="client-nationality-input"
                          />
                        </div>
                      </>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="fiscal_id">Fiscal ID / RFC *</Label>
                      <Input
                        id="fiscal_id"
                        name="fiscal_id"
                        value={formData.fiscal_id}
                        onChange={handleInputChange}
                        required
                        placeholder={clientType === 'Persona Moral' ? '12 characters (e.g. TSO140306ABC)' : '13 characters (e.g. MESA800615XYZ)'}
                        maxLength={clientType === 'Persona Moral' ? 12 : 13}
                        className="uppercase"
                        data-testid="client-fiscal-id-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="you@company.com"
                        data-testid="client-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+52 (55) 0000-0000"
                        data-testid="client-phone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="loan_amount">Loan Amount *</Label>
                      <Input
                        id="loan_amount"
                        name="loan_amount"
                        type="number"
                        value={formData.loan_amount}
                        onChange={handleInputChange}
                        required
                        data-testid="client-loan-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="loan_type">Loan Type</Label>
                      <Select value={formData.loan_type} onValueChange={(v) => handleSelectChange('loan_type', v)}>
                        <SelectTrigger data-testid="client-loan-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Working Capital">Working Capital</SelectItem>
                          <SelectItem value="Business Expansion">Business Expansion</SelectItem>
                          <SelectItem value="Equipment Financing">Equipment Financing</SelectItem>
                          <SelectItem value="Fleet Financing">Fleet Financing</SelectItem>
                          <SelectItem value="Project Financing">Project Financing</SelectItem>
                          <SelectItem value="Renovation Loan">Renovation Loan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="risk_score">Risk Score (0-100)</Label>
                      <Input
                        id="risk_score"
                        name="risk_score"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.risk_score}
                        onChange={handleInputChange}
                        data-testid="client-risk-score-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="risk_category">Risk Category</Label>
                      <Select value={formData.risk_category} onValueChange={(v) => handleSelectChange('risk_category', v)}>
                        <SelectTrigger data-testid="client-risk-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry</Label>
                      <Input
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        placeholder="e.g. Technology"
                        data-testid="client-industry-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="annual_revenue">Annual Revenue</Label>
                      <Input
                        id="annual_revenue"
                        name="annual_revenue"
                        type="number"
                        value={formData.annual_revenue}
                        onChange={handleInputChange}
                        data-testid="client-revenue-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employees">Number of Employees</Label>
                      <Input
                        id="employees"
                        name="employees"
                        type="number"
                        value={formData.employees}
                        onChange={handleInputChange}
                        data-testid="client-employees-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
                        <SelectTrigger data-testid="client-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="defaulted">Defaulted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Address details */}
                    <div className="md:col-span-2 border-t border-border/50 pt-4 mt-2">
                      <h4 className="font-heading font-medium text-sm mb-3">Address Details</h4>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="fiscal_address">Street Address</Label>
                      <Input
                        id="fiscal_address"
                        name="fiscal_address"
                        value={formData.fiscal_address}
                        onChange={handleInputChange}
                        placeholder="Street Name, Boulevard, etc."
                        data-testid="client-street-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="exterior_number">Exterior Number</Label>
                      <Input
                        id="exterior_number"
                        name="exterior_number"
                        value={formData.exterior_number}
                        onChange={handleInputChange}
                        placeholder="e.g. 123"
                        data-testid="client-ext-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="interior_number">Interior Number</Label>
                      <Input
                        id="interior_number"
                        name="interior_number"
                        value={formData.interior_number}
                        onChange={handleInputChange}
                        placeholder="e.g. Apt 4B"
                        data-testid="client-int-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Colonia / Neighborhood</Label>
                      <Input
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleInputChange}
                        placeholder="Colonia name"
                        data-testid="client-neighborhood-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="municipality">Municipality / Delegación</Label>
                      <Input
                        id="municipality"
                        name="municipality"
                        value={formData.municipality}
                        onChange={handleInputChange}
                        placeholder="Municipality"
                        data-testid="client-municipality-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        data-testid="client-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                        data-testid="client-state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">Zip Code</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        placeholder="e.g. 01000"
                        data-testid="client-zip-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        data-testid="client-country-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-6 border-t border-border/50">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(1)}>
                      Back
                    </Button>
                    <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90" data-testid="submit-client-btn">
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Client'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={showWarningModal} onOpenChange={setShowWarningModal}>
            <DialogContent className="max-w-md p-6 text-center" data-testid="profile-warning-modal">
              <DialogHeader>
                <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                  <Activity className="w-6 h-6 text-amber-500" />
                </div>
                <DialogTitle className="font-heading text-xl font-bold text-center">Complete Your Profile First</DialogTitle>
                <DialogDescription className="mt-2 text-sm text-muted-foreground text-center">
                  To comply with financial regulations and enable client verification, you must fill in your profile details (such as your full name, company, and phone number) in Settings before creating a client.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <Button variant="outline" onClick={() => setShowWarningModal(false)} data-testid="warning-modal-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => {
                    setShowWarningModal(false);
                    navigate('/settings');
                  }}
                  data-testid="warning-modal-settings"
                >
                  Update Profile
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('clients.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-clients-input"
          />
        </div>
      </div>

      {/* Table */}
      {filteredClients.length === 0 ? (
        <Card className="p-12 text-center" data-testid="no-clients-card">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-heading text-xl font-semibold mb-2">{t('clients.noClientsFound')}</h3>
        </Card>
      ) : (
        <Card data-testid="clients-table-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('clients.colName')}</TableHead>
                <TableHead>{t('clients.initialLoan')}</TableHead>
                <TableHead>{t('clients.colRisk')}</TableHead>
                <TableHead>{t('clients.colStatus')}</TableHead>
                <TableHead className="text-right">{t('clients.colActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow
                  key={client.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/clients/${client.id}`)}
                  data-testid={`client-row-${client.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{getClientDisplayName(client)}</p>
                        <p className="text-sm text-muted-foreground">{getClientSubName(client)}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{formatCurrency(client.loan_amount)}</p>
                    <p className="text-sm text-muted-foreground">{getTranslatedLoanType(client.loan_type)}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={getRiskBadgeVariant(client.risk_category)}>
                        {getTranslatedRisk(client.risk_category)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">{client.risk_score}/100</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(client.status)}>
                      {getTranslatedStatus(client.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clients/${client.id}`);
                        }}
                        data-testid={`view-client-${client.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={(e) => handleDelete(client.id, e)}
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
